<script setup lang="ts">
import { useRefreshSettingStore } from '@/settings/stores/refreshSettingStore';
import { storeToRefs } from 'pinia';
import { useSettingStore } from 'shared/setting/stores/settingStore';
import { computed, onMounted, useTemplateRef, watch } from 'vue';
import { useShapeLayer } from 'shared/map/layers/shapeLayer';
import { useVehiclesLayer } from 'shared/map/layers/vehicleLayer';
import { usePlacesLayer } from 'shared/map/layers/placesLayer';
import { usePackagesLayer } from 'shared/map/layers/packagesLayer';
import { useRefreshShapeStore } from '@/shapes/stores/refreshShapeStore';
import { useRefreshVehicleStore } from '@/vehicles/stores/refreshVehicleStore';
import { useRefreshCoilStore } from '@/coils/stores/refreshCoilStore';
import { useRefreshStorageAreaStore } from '@/storageAreas/stores/refreshStorageAreaStore';
import { useRefereshProductStore } from '@/products/commands/refreshProductStore';
import { useRefereshProductGroupStore } from '@/products/groups/commands/refreshProductGroupStore';
import { useRefreshPackageStore } from '@/packages/commands/refreshPackageStore';
import type { MapStyle } from 'shared/mapStyles/domain/mapStyle';
import OLMap from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import { useGeographic } from 'ol/proj'; 

useGeographic();
const rootElementRef = useTemplateRef('map-view');
const settingStore = useSettingStore();
const refreshSettingStore = useRefreshSettingStore();
const refreshShapeStore = useRefreshShapeStore();
const refreshStorageAreaStore = useRefreshStorageAreaStore();
const refreshVehicleStore = useRefreshVehicleStore();
const refreshCoilStore = useRefreshCoilStore();
const refreshProductStore = useRefereshProductStore();
const refreshProductGroupStore = useRefereshProductGroupStore();
const refreshPackageStore = useRefreshPackageStore();

const props = defineProps<{
  mapStyle: MapStyle
}>();

const mapStyle = computed(() => props.mapStyle);

const shapeLayer = useShapeLayer({
  mapStyle: mapStyle
});

const vehiclesLayer = useVehiclesLayer({
  mapStyle: mapStyle
});

const placesLayer = usePlacesLayer({
  mapStyle: mapStyle
});

const packagesLayer = usePackagesLayer({
  mapStyle: mapStyle
});

onMounted(async () => {
  await setupMap();
  await refreshStores();
});

async function setupMap() {
  const rootElement = rootElementRef.value;
  if (!rootElement) throw new Error('Map div not found!');

  const { mapSettings } = storeToRefs(settingStore);
  await refreshSettingStore.initialize();

  const tileLayer = new TileLayer({
    source: new XYZ({
      url: 'api/map/osm/tile/{z}/{x}/{y}'
    })
  });

  const center = [mapSettings.value.longitude, mapSettings.value.latitude];

  const zoom = mapSettings.value.zoom;

  const bearing = mapSettings.value.bearing;

  const view = new View({
    center: center,
    zoom: zoom,
    rotation: bearing,
    minZoom: mapSettings.value.minZoom ?? 3,
  });

  const map = new OLMap({
    target: rootElement,
    view,
    layers: [tileLayer]
  });

  watch(mapSettings, (mapSettings) => {
    view.animate({
      center: [mapSettings.longitude, mapSettings.latitude],
      rotation: mapSettings.bearing,
      zoom: mapSettings.zoom
    });
  });

  shapeLayer.attach(map);
  vehiclesLayer.attach(map);
  placesLayer.attach(map);
  packagesLayer.attach(map);
}

async function refreshStores() {
  await refreshProductStore.initialize();
  await refreshStorageAreaStore.initialize();
  await refreshShapeStore.initialize();
  await refreshVehicleStore.initialize();
  await refreshCoilStore.initialize();
  await refreshProductGroupStore.initialize();
  await refreshPackageStore.initialize();
}

</script>

<template>
  <div class="map-view" ref="map-view"></div>
</template>

<style scoped>
.map-view {
  height: 100%;
}
</style>
